/**
 * Cross-platform color math for the shared UI lib.
 *
 * Web can lean on the browser's `color-mix()` at render time, but native
 * (`react-native-svg` / NativeWind) has no equivalent — so any component that
 * needs to derive a tint/shade from an arbitrary runtime hex (e.g.
 * `MenuListItem`'s `accentColor`) must precompute the result up-front. These
 * helpers are the single source of that math; they were lifted from
 * flashka-app's `deck-colors.ts` so web + native land on the same hex.
 */

import { BRAND_HEX } from '../styles/brand-colors';

/**
 * RN port of CSS `color-mix(in srgb, hex, target amount%)`. Linearly blends a
 * hex color toward white or black. `amount` is the 0..1 fraction of the target
 * to mix in (e.g. `0.9` = 90% white → a pale tint). Non-6-digit hex inputs are
 * returned unchanged so callers can pass `currentColor`/named colors safely on
 * the web path (where this helper is never the one doing the mixing).
 */
export function mixHex(hex: string, mixWith: 'white' | 'black', amount: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  const target = mixWith === 'white' ? 255 : 0;
  const ratio = Math.max(0, Math.min(1, amount));
  const mix = (c: number) => Math.round(c * (1 - ratio) + target * ratio);
  const toHex = (c: number) => mix(c).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * An identity colour's tint for an ART SURFACE — a deck's emoji tile, a
 * folder's preview page. Light mixes toward white: the pastel the design
 * draws. Dark mixes the same amount toward black, the rule web's deck glyph
 * has applied to custom colours since dark mode shipped, so a pastel never
 * lands as a glaring slab on the `#090909` page.
 */
export function tintHex(hex: string, amount: number, isDark: boolean): string {
  return mixHex(hex, isDark ? 'black' : 'white', amount);
}

/**
 * The facepile `+N` counter's tint pair for a runtime hex: a pale white-mix
 * ground under text darkened from the same hue (the deck-preview family).
 * ONE recipe for every overflow counter (StudyGroupCard, MemberAvatarStack)
 * so they cannot drift apart again.
 */
export function counterTint(hex: string): { backgroundColor: string; color: string } {
  return { backgroundColor: mixHex(hex, 'white', 0.6), color: mixHex(hex, 'black', 0.2) };
}

/**
 * A hex colour at a given alpha, as `rgba()`.
 *
 * Exists so a translucent surface can be an INLINE STYLE rather than an alpha
 * utility class (`bg-secondary-50/70`). Those are runtime-processed by
 * NativeWind, and swapping one on a live element is the bogus "Couldn't find a
 * navigation context" crash — so any translucent surface whose value changes
 * with a prop has to be expressed this way. See `restyleKey`'s docblock.
 *
 * Non-6-digit hex is returned unchanged, matching {@link mixHex}, so callers
 * can pass `currentColor` / named colours on the web path safely.
 */
export function withAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}

/**
 * True when `color` is an explicit hex string (user-chosen custom color)
 * rather than a named-palette token.
 */
export function isCustomHexColor(color: string | null | undefined): boolean {
  return !!color && color.startsWith('#');
}

/**
 * Every brand palette's `-400 → -700` pair, keyed by the -400 hex. Deck
 * identity colours ARE the -400 shades (`deck-lime` = `lime-400` = `#9ae600`),
 * so a deck hex maps straight onto its palette's -700.
 */
const SHADE_700_BY_400 = new Map(
  Object.values(BRAND_HEX).map((palette) => [
    palette['400'].toLowerCase(),
    palette['700'],
  ]),
);

/**
 * The `-700` shade of a deck identity colour — the readable-on-tint text tone
 * (DeckCard's emoji/tag label over its 30%-white tile).
 *
 * ponytail: user-picked CUSTOM hexes aren't on the brand ramp, so they fall
 * back to a flat 50% black mix. Swap for an OKLCH ramp if a custom colour ever
 * reads visibly off against its palette siblings.
 */
export function shade700(hex: string): string {
  return SHADE_700_BY_400.get(hex.toLowerCase()) ?? mixHex(hex, 'black', 0.5);
}
